import { ITextStyles } from '@fluentui/react';
import Tag from 'models/tag';

import { IPIconButton, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { cancelIcon, globalStackTokensGapExtraSmall } from 'globalStyles';
import { useTranslation } from 'react-i18next';
import { getBestForGroundColor } from 'utils/colorHelpers';

interface IKeyValueTagProps {
  tag: Tag;
  onRemove?: (item: Tag) => void;
  onClick?: (item: Tag) => void;
  compact?: boolean;
  width?: number;
  maxWidth?: number; //set this to set a maximum width for example when the parent container has a maximum size
  showBorder?: boolean;
  borderColor?: string;
  showCategory?: boolean; //if true, the category rendered behind the tag
}

export const KeyValueTag = (props: IKeyValueTagProps) => {
  const { t } = useTranslation(['translation', 'tag']);

  const getTagStyles = (tag: Tag) => {
    return {
      root: {
        background: tag.tagColor,
        borderRadius: '10px',
        border: props.showBorder ? '1px solid' : undefined,
        borderColor: props.borderColor ? props.borderColor : 'blue',
        margin: '2px',
        height: props.compact ? '18px' : '22px',
      },
    };
  };

  const getTagButtonStyles = (tag: Tag) => {
    return {
      root: {
        borderRadius: '8px',
        height: props.compact ? '18px' : '22px',
        width: props.compact ? '18px' : '22px',
      },
    };
  };

  const getStackItemStyles = (tag: Tag) => {
    if (props.onClick) {
      return {
        root: {
          selectors: {
            '&:hover': {
              cursor: 'pointer',
            },
          },
          width: props.width,
          maxWidth: props.maxWidth,
        },
      };
    } else {
      return {
        root: {
          width: props.width,
          maxWidth: props.maxWidth,
        },
      };
    }
  };

  const getTagTextStyles = (tag: Tag): ITextStyles => {
    return {
      root: {
        marginLeft: '5px',
        marginRight: '5px',
        color: getBestForGroundColor(tag),
      },
    };
  };

  const getTag = () => {
    return (
      <IPStack
        key={'tag' + props.tag.tagId.toString()}
        horizontal
        styles={getTagStyles(props.tag)}
        verticalAlign="start"
        horizontalAlign="space-between"
      >
        <IPStack.Item
          grow
          style={{ overflow: 'hidden' }} //this ensures that the text is cut off nicely, otherwise it will run out of the stack.item
          styles={getStackItemStyles(props.tag)}
          onClick={() => {
            if (props.onClick) {
              props.onClick(props.tag);
            }
          }}
        >
          <IPTooltipHost content={props.tag.tagInfo}>
            <IPText
              block
              nowrap
              styles={getTagTextStyles(props.tag)}
              key={props.tag.tagId.toString()}
              variant={props.compact ? 'small' : 'medium'}
            >
              {props.tag.value()}
            </IPText>
          </IPTooltipHost>
        </IPStack.Item>

        {props.onRemove && (
          <IPStack.Item>
            <IPIconButton
              onClick={() => {
                if (props.onRemove) {
                  props.onRemove(props.tag);
                }
              }}
              styles={getTagButtonStyles(props.tag)}
              iconProps={cancelIcon}
            />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (props.showCategory) {
    return (
      <IPStack horizontal tokens={globalStackTokensGapExtraSmall} verticalAlign="center">
        {getTag()}
        <IPStack.Item>
          <IPText block nowrap key={`category-${props.tag.tagId.toString()}`} variant={'tiny'}>
            {Tag.GetSystemTypeText(props.tag.tagSystemType, t)}
          </IPText>
        </IPStack.Item>
      </IPStack>
    );
  } else {
    return getTag();
  }
};

interface IKeyValueTagsProps {
  tags: Tag[];
  onClick?: (item: Tag) => void;
  compact?: boolean;
  tagWidth?: number; //set this to stretch all tasks to exactly this width
  tagMaxWidth?: number;
}

export const KeyValueTags = (props: IKeyValueTagsProps) => {
  return (
    <IPStack horizontal wrap tokens={globalStackTokensGapExtraSmall} styles={{ root: { marginRight: 5 } }}>
      {props.tags?.map((_tag, index) => {
        return (
          <IPStack.Item key={`tag-${_tag.tagId}-${index}`} styles={{ root: { margin: 0 } }}>
            <KeyValueTag
              compact={props.compact}
              width={props.tagWidth}
              maxWidth={props.tagMaxWidth}
              tag={_tag}
              onClick={props.onClick}
            />
          </IPStack.Item>
        );
      })}
    </IPStack>
  );
};
